/**
 * useAct2Loop — 第二幕游戏循环 Hook
 *
 * v2: 支持 SSE 流式对话，降级回退到 JSON 模式。
 */
import { useCallback, useRef } from 'react';
import { useAct2Store } from '../stores/act2Store';
import { useRootStore } from '../stores/rootStore';
import { act2Chat, act2Cards, act2Evaluate } from '../services/api/v2GameApi';
import { connectSSE } from '../services/api/sseClient';
import { FlowController } from '../utils/flowController';
import type { Act2Card } from '@shared/types/cards';
import type { RoundSummary } from '@shared/types/agent';
import {
  ACT2_MAX_ROUNDS,
  ACT2_CARD_GEN_INTERVAL,
  ACT2_AFFECTION_FAIL,
  ACT2_COMFORT_FAIL,
  ACT2_HAND_SIZE,
} from '@shared/constants/gameBalance';

export function useAct2Loop() {
  const act2 = useAct2Store();
  const root = useRootStore();
  const flowControllerRef = useRef(new FlowController());
  const abortRef = useRef<AbortController | null>(null);

  /** 检查是否提前结束 */
  const checkEarlyEnd = useCallback((): boolean => {
    const s = useAct2Store.getState();
    const avgAff = (s.metricsA.affection + s.metricsB.affection) / 2;
    const avgComf = (s.metricsA.comfort + s.metricsB.comfort) / 2;
    return avgAff < ACT2_AFFECTION_FAIL || avgComf < ACT2_COMFORT_FAIL;
  }, []);

  /** 请求 LLM 生成新卡牌 */
  const fetchCards = useCallback(async () => {
    const rootState = useRootStore.getState();
    const store = useAct2Store.getState();
    if (!rootState.protagonistA || !rootState.protagonistB) return;
    try {
      const res = await act2Cards({
        protagonistA: rootState.protagonistA,
        protagonistB: rootState.protagonistB,
        history: store.messages,
        metricsA: store.metricsA,
        metricsB: store.metricsB,
        topicStage: store.topicStage,
        scene: store.scene,
        usedTopics: store.usedTopics,
      });
      store.addCardsToDeck(res.cards);
      store.drawToHand(ACT2_HAND_SIZE - store.hand.length);
    } catch (err) {
      console.error('Act2 card gen failed:', err);
    }
  }, []);

  /** 应用 metrics 变化 */
  const applyMetricChanges = useCallback((summary: RoundSummary) => {
    const store = useAct2Store.getState();
    if (summary.metricsChangeA) store.updateMetrics('A', summary.metricsChangeA);
    if (summary.metricsChangeB) store.updateMetrics('B', summary.metricsChangeB);
  }, []);

  /** 推进轮次 */
  const advanceRound = useCallback(async () => {
    const store = useAct2Store.getState();

    if (checkEarlyEnd()) {
      await finishAct2();
      return;
    }

    const nextRound = store.round + 1;
    store.nextRound();

    if (nextRound % ACT2_CARD_GEN_INTERVAL === 0) {
      await fetchCards();
    }

    if (nextRound > ACT2_MAX_ROUNDS) {
      await finishAct2();
      return;
    }

    store.setPhase('PLAYER_TURN');
  }, [checkEarlyEnd, fetchCards]);

  /** 降级到 JSON 模式 */
  const fallbackToJsonMode = useCallback(async (playedCard?: Act2Card) => {
    const rootState = useRootStore.getState();
    const store = useAct2Store.getState();
    if (!rootState.protagonistA || !rootState.protagonistB) return;

    try {
      const response = await act2Chat({
        protagonistA: rootState.protagonistA,
        protagonistB: rootState.protagonistB,
        history: store.messages,
        round: store.round,
        metricsA: store.metricsA,
        metricsB: store.metricsB,
        topicStage: store.topicStage,
        scene: store.scene,
        playedCard,
        act1Result: rootState.act1Result ?? undefined,
      });

      store.setIsTyping(false);
      store.clearStreaming();

      for (const msg of response.messages) {
        store.appendMessage(msg);
        await new Promise(r => setTimeout(r, 300));
      }

      if (response.metricsChangeA) store.updateMetrics('A', response.metricsChangeA);
      if (response.metricsChangeB) store.updateMetrics('B', response.metricsChangeB);

      if (playedCard?.targetTopic) {
        store.addUsedTopic(playedCard.targetTopic);
      }

      await advanceRound();
    } catch (err) {
      console.error('Act2 fallback also failed:', err);
      store.setIsTyping(false);
      store.clearStreaming();
      store.setPhase('PLAYER_TURN');
    }
  }, [advanceRound]);

  /** 执行一轮对话（SSE 流式模式） */
  const executeRound = useCallback(async (playedCard?: Act2Card) => {
    const rootState = useRootStore.getState();
    if (!rootState.protagonistA || !rootState.protagonistB) return;

    const store = useAct2Store.getState();
    store.setPhase('PROCESSING');
    store.setIsTyping(true);

    flowControllerRef.current.interrupt();
    if (abortRef.current) abortRef.current.abort();

    const flowToken = flowControllerRef.current.getToken();

    const requestBody = {
      protagonistA: rootState.protagonistA,
      protagonistB: rootState.protagonistB,
      history: store.messages,
      round: store.round,
      metricsA: store.metricsA,
      metricsB: store.metricsB,
      topicStage: store.topicStage,
      scene: store.scene,
      playedCard,
      act1Result: rootState.act1Result ?? undefined,
    };

    const abortCtrl = connectSSE('/api/act2/round', requestBody, {
      onCardNarrativeChunk: (text) => {
        if (!flowToken.isValid()) return;
        const s = useAct2Store.getState();
        s.setIsCardNarrating(true);
        s.appendCardNarrative(text);
      },

      onCardNarrativeEnd: (cardName, _cardType, fullText) => {
        if (!flowToken.isValid()) return;
        const s = useAct2Store.getState();
        s.commitCardNarrative(cardName, fullText);
      },

      onSpeechStart: (agentId, agentName, agentRole) => {
        if (!flowToken.isValid()) return;
        const s = useAct2Store.getState();
        s.setCurrentSpeaker(agentId);
        s.setIsTyping(true);
        s.setTypingSpeaker(agentId);
        s.clearStreaming();
        s.setStreamingAgent(agentName, agentRole);
      },

      onSpeechChunk: (_agentId, text) => {
        if (!flowToken.isValid()) return;
        useAct2Store.getState().appendStreamingText(text);
      },

      onSpeechEnd: () => {
        if (!flowToken.isValid()) return;
        useAct2Store.getState().commitStreamingMessage();
      },

      onRoundComplete: (summary) => {
        if (!flowToken.isValid()) return;
        const s = useAct2Store.getState();
        applyMetricChanges(summary);
        s.setIsTyping(false);
        s.setCurrentSpeaker(null);
        s.clearStreaming();

        if (playedCard?.targetTopic) {
          s.addUsedTopic(playedCard.targetTopic);
        }

        advanceRound();
      },

      onError: (err) => {
        console.error('Act2 SSE error, falling back to JSON:', err);
        fallbackToJsonMode(playedCard);
      },
    });

    abortRef.current = abortCtrl;
  }, [applyMetricChanges, advanceRound, fallbackToJsonMode]);

  /** 结束第二幕 */
  const finishAct2 = useCallback(async () => {
    const rootState = useRootStore.getState();
    const store = useAct2Store.getState();
    if (!rootState.protagonistA || !rootState.protagonistB) return;

    store.setPhase('FINISHED');

    try {
      const response = await act2Evaluate({
        protagonistA: rootState.protagonistA,
        protagonistB: rootState.protagonistB,
        history: store.messages,
        metricsA: store.metricsA,
        metricsB: store.metricsB,
        topicStage: store.topicStage,
      });

      rootState.finishAct2(response.result);
    } catch (err) {
      console.error('Act2 evaluate failed:', err);
      rootState.finishAct2({
        outcome: '暧昧入场',
        finalMetricsA: store.metricsA,
        finalMetricsB: store.metricsB,
        topicCompatibility: 50,
        highlights: ['约会在平淡中结束'],
        commitmentBonus: 0,
        stabilityBonus: 0,
      });
    }
  }, []);

  /** 玩家出牌 */
  const playCard = useCallback((card: Act2Card) => {
    const removed = useAct2Store.getState().playCard(card.id);
    if (!removed) return;
    executeRound(removed);
  }, [executeRound]);

  /** 玩家跳过 */
  const skip = useCallback(() => {
    executeRound();
  }, [executeRound]);

  /** 开始第一轮 */
  const startFirstRound = useCallback(async () => {
    const store = useAct2Store.getState();
    store.setPhase('PROCESSING');
    store.appendMessage({
      id: `sys_act2_opening_${Date.now()}`,
      senderId: 'system',
      senderName: '系统',
      senderRole: 'system',
      text: '两人来到约会地点，二人世界正式开始...',
      type: 'system',
    });

    await fetchCards();
    await executeRound();
  }, [fetchCards, executeRound]);

  return {
    playCard,
    skip,
    startFirstRound,
    phase: act2.phase,
    round: act2.round,
  };
}
